﻿import {$, $$, clickOnEnterKey, get, getDataJson, getDataText, hide, highlight, toggle} from "@/Utils";
import {checkTransportRestriction, setTransportRestrictionStyles} from "@/CartModule";
import {Country, DataAttributes} from "@/enums";
import {AddressDto, AddressResponseDto, EventDetail} from "@/interfaces";
import {triggerDropdownClick} from "@/sharedComponents/Dropdown/Dropdown";

export function addValidationError(addressComponent: Element) {
    const inAddressField = addressComponent.querySelector(".address-field") as HTMLInputElement;
    inAddressField?.classList.add("input-validation-error");
    inAddressField?.parentElement.parentElement.nextElementSibling?.classList.replace("field-validation-valid", "field-validation-error");
}

export function removeValidationError(addressComponent: Element) {
    const inAddressField = addressComponent.querySelector(".address-field") as HTMLInputElement;
    inAddressField?.classList.remove("input-validation-error");
    inAddressField?.parentElement.parentElement.nextElementSibling?.classList.replace("field-validation-error", "field-validation-valid");
}


export function initAddressSearchComponent(addressComponent: HTMLElement, component: HTMLElement, updateAddressFields: Function, queryAddresses: Function) {
    if (!component) return;
    const accordionItem = component.closest(".accordion__item");
    if (accordionItem && !accordionItem.classList.contains("accordion__item--active")) return;
    const addressField = component.querySelector(".address-field") as HTMLInputElement;
    let timeout: number = null;

    const addressFieldInputListener = (e: Event) => {
        clearTimeout(timeout);
        const addressSearchField = e.currentTarget as HTMLInputElement;
        const searchString = addressSearchField.value;

        // clear address fields if search has been removed
        if (!searchString.length) {
            updateAddressFields(addressComponent);
        }
        // do not search under 3 chars
        if(searchString.length<3){
            return;
        }

        timeout = window.setTimeout(() => queryAddresses(searchString, addressComponent), 750);
    }

    addressField.addDataBoundEventListener("input", addressFieldInputListener);

    const addressList = component.querySelector(".autocomplete-list") as HTMLElement;

    const addressListClickListener = (e: Event) => {
        let target = e.target as HTMLElement;
        if (!target.hasAttribute("data-address")) {
            target = target.parentElement; //<mark> click
        }

        if (!target) return;

        addressField.value = target.getAttribute("data-formatted-address");

        updateAddressFields(addressComponent, target);

        hide(addressList);
    }

    addressList.addDataBoundEventListener("click", addressListClickListener);
}

export function initAddressFields(addressComponent: HTMLElement) {
    const inAddress = addressComponent.querySelector(".address-search");
    const manualAddressContainer = addressComponent.querySelector(".manual-address-container");

    const searchAddress = addressComponent.querySelector("[data-search-address]") as HTMLElement;
    const manualAddress = addressComponent.querySelector(".address-manually") as HTMLElement;

    const addressInput = addressComponent.querySelector(".input-address") as HTMLInputElement;
    const postalCodeInput = addressComponent.querySelector(".input-postalcode") as HTMLInputElement;
    
    const addressComponentPrefix = getAddressComponentPrefix(addressComponent);

    const countyInput = get(`${addressComponentPrefix}_County`) as HTMLInputElement;
    const cityInput = get(`${addressComponentPrefix}_City`) as HTMLInputElement;
    const countyDropdown = addressComponent.querySelector(".dropdown-county");
    const cityDropdown = addressComponent.querySelector(".dropdown-city");
    const settlementDropdown = addressComponent.querySelector(".dropdown-settlement");

    const apartmentInput = addressComponent.querySelector(".input-apartment") as HTMLInputElement;


    let inputs = [
        addressInput,
        countyInput,
        cityInput,
        postalCodeInput
    ];
    if (settlementDropdown) {
        inputs.push(settlementDropdown.previousElementSibling as HTMLInputElement)
    }

    const enableInputs = () => inputs.forEach(i => i ? i.disabled = false : null);
    enableInputs();

    const apartmentNumberListener = () => {
        //only fill when address has already been entered
        if (!addressInput.value) {
            return;
        }
        const apartmentNo = apartmentInput?.value;
        addressInput.setAttribute("data-apartment_nr", apartmentNo ?? "");

        let address = addressInput.getAttribute("data-address");        
        const buildingNo = addressInput.getAttribute("data-building-no");

        if (!address) {
            address = addressInput.value; // if no data attribute (e.g after edit), get it from input
        }
        //Remove apartment number from address if it exists
        const parts = address.split(/( k-\d{1,4})?-\d{1,4}/);
        address = parts.length > 1 ? parts.slice(0,-1).join('') : address;

        addressInput.value = formattedAddressInput(address, buildingNo, apartmentNo)

    }

    const manualAddressClickListener = (e: Event) => {
        inAddress.classList.add("address-search__hidden");
        manualAddressContainer.classList.remove("manual-address__hidden");

        if (cityDropdown?.querySelectorAll(".dropdown__list-item").length < 2) {
            hideDropdown(cityDropdown);
        }

        if (settlementDropdown?.querySelectorAll(".dropdown__list-item").length < 2) {
            hideDropdown(settlementDropdown);
        }

        const target = e?.currentTarget as Element;
        const addressField = target?.closest(".address-search").querySelector(".address-field") as HTMLInputElement;
        if (addressField?.classList.contains("input-validation-error")) {
            removeValidationError(addressComponent);
            addressField.setAttribute("data-error", "true");
        }
        setTransportRestrictionStyles();
        zipCodeHandler(addressComponent, addressComponentPrefix);
    }

    const searchAddressClickListener = (e: Event) => {
        inAddress.classList.remove("address-search__hidden");
        manualAddressContainer.classList.add("manual-address__hidden");
        const target = e.currentTarget as Element;
        const addressField = target.closest(".address__component").querySelector(".address-search .address-field") as HTMLInputElement;
        if (addressField.hasAttribute("data-error")) {
            addValidationError(addressComponent);
            addressField.removeAttribute("data-error");
        }
        setTransportRestrictionStyles();
    }

    searchAddress?.addDataBoundEventListener("click", searchAddressClickListener);
    manualAddress?.addDataBoundEventListener("click", manualAddressClickListener);
    searchAddress?.addDataBoundEventListener("keydown", (e: KeyboardEvent) => {
        clickOnEnterKey(e, searchAddress)
    }, "enter-keypress-bound");
    manualAddress?.addDataBoundEventListener("keydown", (e: KeyboardEvent) => {
        clickOnEnterKey(e, manualAddress)
    }, "enter-keypress-bound");
    apartmentInput?.addDataBoundEventListener("change", apartmentNumberListener);
    if (!inAddress) {
        manualAddressClickListener(null);
    }

    countyDropdown?.addDataBoundEventListener("valueChange", (e: CustomEvent<EventDetail>) => {
        const newCounty = e.detail.value;
        updateCountyDropdown(addressComponent.querySelector(`.dropdown__list-item[value="${newCounty}"]`));
        updateCityDropdown(newCounty, addressComponent);
        if (settlementDropdown) {
            updateSettlementDropdown(null, addressComponent);
        }
        clearInputs(addressInput, postalCodeInput);
        const settlementInput = get(`${addressComponentPrefix}_Settlement`) as HTMLInputElement;
        const cityInput = get(`${addressComponentPrefix}_City`) as HTMLInputElement;
        if (settlementInput) {
            settlementInput.value = null;
        }
        if (cityInput) {
            cityInput.value = null;
        }
        enableInputs();
    }, "valuechange");

    cityDropdown?.addDataBoundEventListener("valueChange", (e: CustomEvent<HTMLInputElement>) => {
        const newCity = e.detail.value;
        if (!newCity) return;
        
        const settlementCallback = () => {
            zipCodeHandler(addressComponent, addressComponentPrefix)
        }
        
        if (settlementDropdown) {
            updateSettlementDropdown(newCity, addressComponent, settlementCallback)
        }
        clearInputs(addressInput, postalCodeInput);
        const settlementInput = get(`${addressComponentPrefix}_Settlement`) as HTMLInputElement;
        if (settlementInput) {
            settlementInput.value = null;
        }
        enableInputs();
        if (!settlementDropdown) {
            settlementCallback()
        }
    }, "valuechange");

    settlementDropdown?.addDataBoundEventListener("valueChange", () => {
        clearInputs(addressInput, postalCodeInput);
        enableInputs();
        zipCodeHandler(addressComponent, addressComponentPrefix);
    }, "valuechange");
}

function formatCounty(county: string) {
    return county.replace(" maakond", "maa").toUpperCase();
}

function formatSettlement(settlement: string) {
    return settlement.replace(" linn", "").replace(" alevik", "").replace(" alev", "").replace(" küla", "");
}

function clearInputs(addressInput: HTMLInputElement, postCodeInput: HTMLInputElement) {
    addressInput.value = null;
    postCodeInput.value = null;
    postCodeInput.removeAttribute(DataAttributes.NoTransport);
    postCodeInput.classList.remove("no-transport");
    setTransportRestrictionStyles();
}


function zipCodeHandler(addressComponent : HTMLElement, addressComponentPrefix: string) {
    if (window.config.countryCode != Country.EE) {
        return
    }
    const countyInput = get(`${addressComponentPrefix}_County`) as HTMLInputElement;
    const cityInput = get(`${addressComponentPrefix}_City`) as HTMLInputElement;
    const settlementInput = get(`${addressComponentPrefix}_Settlement`) as HTMLInputElement;

    const county = countyInput.value;
    const city = cityInput.value === $(".dropdown-city .dropdown__selected [data-text]").textContent.trim() ? cityInput.value : "";
    const settlement = (settlementInput && settlementInput.value === $(".dropdown-settlement .dropdown__selected [data-text]").textContent.trim()) ? settlementInput.value : "";

    let area: string;

    const settlements = addressComponent.querySelectorAll(".dropdown-settlement .dropdown__list-item");
    // !items.length true when city does not contain settlements (Kohtla-Järve linn)
    const cityNoSuffix = city.replace(" linn", "").replace(" vald", "")
    if (county.length && !settlement.length && city.length && !city.includes(" vald") && !settlements.length) {
        area = formatCounty(county) + "-" + cityNoSuffix;
    } else if (county.length && settlement.length && (city.includes(" vald") || city.includes(" linn"))) {
        area = formatCounty(county) + "-" + cityNoSuffix + "-" + formatSettlement(settlement);
    } else {
        return
    }

    const zipCodesRequest = window.config.getZipCodesUrl + "?area=" + area;
    const zipInput = get(`${addressComponentPrefix}_PostalCode`) as HTMLInputElement;

    const nextButton = get("shipping-next");
    nextButton?.setAttribute("disabled","disabled");

    getDataJson(zipCodesRequest, res => {
        const zipCodes = res as string[];
        zipInput.setAttribute("data-val-custom", get("wrong-index-message").textContent)
        zipInput.setAttribute("data-val-regex", "*")
        zipInput.setAttribute("data-val-regex-pattern", zipCodes.join("|"))
        if (zipCodes.length === 1) {
            zipInput.readOnly = true;
            zipInput.value = zipCodes[0];
            zipInput.classList.remove("input-validation-error")
            zipInput.nextElementSibling.classList.replace("field-validation-error", "field-validation-valid")
            checkTransportRestriction(zipInput);
            return;
        } else {
            zipInput.readOnly = null;
        }
    }).then(() => { nextButton?.removeAttribute("disabled"); });
}

export function formatAddress(address: AddressDto, searchString: string, apartmentNumber: string = null) {
    apartmentNumber = apartmentNumber ? apartmentNumber : address.apartmentNr;
    const formatted = address.formatted.replace("-#", !!apartmentNumber ? `-${apartmentNumber}` : "");

    const highlighted = highlight(formatted, searchString);

    return `<li>
                <button type='button' class='autocomplete-list__result'
                    data-formatted-address="${formatted}"
                    data-adr_id="${address.adrId}"
                    data-address="${address.address ?? ""}"
                    data-settlement="${address.settlement ?? ""}"
                    data-city="${address.city ?? ""}"
                    data-county="${address.county ?? ""}"
                    data-apartment_nr="${apartmentNumber ?? ""}"
                    data-postalcode="${address.postalCode ?? ""}"
                    data-building-no="${address.buildingNo ?? ""}">
                    ${highlighted}
                </button/>
            </li>`;
}

export function processResponse(response: AddressResponseDto, searchString: string, addressComponent: Element, onLoadValidation: boolean) {
    const addressList = addressComponent.querySelector(".autocomplete-list") as HTMLElement; // TODO: data-attribute, CSS toggle
    const apartments = new Map<string, Array<string>>();
    const apartmentInput = addressComponent.querySelector(".input-apartment") as HTMLInputElement;

    let addressHtml = "";

    if (response.addresses) {
        removeValidationError(addressComponent);
        const distinctAddresses = response.addresses.filter((val, index, self) => self.map(x => x.adrId).indexOf(val.adrId) === index);

        distinctAddresses.forEach(address => {
            if (address.apartments && !!address.apartments.length) {
                apartments[address.adrId] = address.apartments.map(x => x.number);
            }

            if (apartments[address.adrId]) {
                const matchingApartments = apartments[address.adrId].filter(x => !address.apartmentNr || x.startsWith(address.apartmentNr)).slice(0, 10);

                for (const aptNo of matchingApartments) {
                    addressHtml += formatAddress(address, searchString, aptNo);
                }
            } else {
                addressHtml += formatAddress(address, searchString, apartmentInput?.value);
            }
        });
        addressList.innerHTML = addressHtml;
        toggle(addressList, !onLoadValidation);
    } else {
        addValidationError(addressComponent);
        hide(addressList);
    }
}

/* Updates Address fields from provided data*/
export async function updateAddressFields(addressComponent: HTMLElement, addressListElement: HTMLElement = null) {

    const addressComponentPrefix = getAddressComponentPrefix(addressComponent);
    const countyInput = get(`${addressComponentPrefix}_County`) as HTMLInputElement;
    const cityInput = get(`${addressComponentPrefix}_City`) as HTMLInputElement;
    const addressInput = addressComponent.querySelector(".input-address") as HTMLInputElement;
    const postalCodeInput = addressComponent.querySelector(".input-postalcode") as HTMLInputElement;
    const settlementHidden = addressComponent.querySelector(".dropdown-settlement")?.previousElementSibling as HTMLInputElement;


    if (addressListElement) {
        const county = addressListElement.getAttribute("data-county");
        const city = addressListElement.getAttribute("data-city");
        const settlement = addressListElement.getAttribute("data-settlement");

        addressInput.disabled = false;
        if (settlementHidden) {
            settlementHidden.value = settlement;
            settlementHidden.disabled = false;
        }

        const apartmentNo = addressListElement.getAttribute("data-apartment_nr");
        const address = addressListElement.getAttribute("data-address");
        const buildingNo = addressListElement.getAttribute("data-building-no");

        if (!!cityInput) {
            cityInput.value = city;
            cityInput.disabled = false;
        }
        
        countyInput.value = county;
        countyInput.disabled = false;
        addressInput.value = formattedAddressInput(address, buildingNo, apartmentNo)

        addressInput.setAttribute("data-address", address);
        addressInput.setAttribute("data-apartment_nr", apartmentNo);
        addressInput.setAttribute("data-building-no", buildingNo);

        postalCodeInput.value = addressListElement.getAttribute("data-postalcode");
        postalCodeInput.disabled = false;

        const addressField = addressComponent.querySelector(".address-field");
        checkTransportRestriction(postalCodeInput, addressField);
        const countyElement = addressComponent.querySelector(`.dropdown-county .dropdown__list-item[value="${county}"]`) as HTMLElement;
        if (countyElement) {
            if (countyElement.hasAttribute(DataAttributes.NoTransport)) {
                addressField.setAttribute(DataAttributes.NoTransport, "");
            }
            triggerDropdownClick(countyElement, false);
            updateCountyDropdown(countyElement);
            const cityCallback = () => {
                const cityItem = addressComponent.querySelector(`.dropdown-city .dropdown__list-item[value="${city}"]`) as HTMLElement;
                if (cityItem) {
                    triggerDropdownClick(cityItem, false);
                    if (settlementHidden) {
                        const settlementCallback = () => {
                            const settlementItem = addressComponent.querySelector(`.dropdown-settlement .dropdown__list-item[value="${settlement}"]`) as HTMLElement;
                            if (settlementItem) {
                                triggerDropdownClick(settlementItem, false);
                            }
                        }
                        updateSettlementDropdown(city, addressComponent, settlementCallback);
                    }
                }
            }
            updateCityDropdown(county, addressComponent, cityCallback);
        }

    }else {
        const inputs = [addressInput, settlementHidden, cityInput, countyInput, postalCodeInput];
        inputs.filter(i => i != null).forEach(i => {
            i.value = "";
            i.disabled = true;
        });
    }
}

function formattedAddressInput(address: string, buildingNo: string, apartmentNo: string){
    return `${address ?? ""}${!!buildingNo ? " k-" + buildingNo : ""}${!!apartmentNo ? '-' + apartmentNo : ''}`;
}

export function queryAddresses(searchString: string, addressComponent: Element, onLoadValidation = false, manual = false) {
    const url = new URL(window.config.addressSearchUrl, window.config.nativeHomeUrl);
    if(manual) {
        url.searchParams.append("manual", "true");
    }
    
    
    url.searchParams.append("query", searchString);

    const callback = response => {
        processResponse(response, searchString, addressComponent, onLoadValidation);
    }
    
    getDataJson(url, callback, true).then(() => {});
}

function updateCountyDropdown(countyElement: HTMLElement) {
    if (get("shipping-restriction-message")) {
        const dropdown = countyElement.closest(".dropdown");
        if (countyElement.hasAttribute(DataAttributes.NoTransport)) {
            dropdown.setAttribute(DataAttributes.NoTransport, "");
        } else {
            dropdown.removeAttribute(DataAttributes.NoTransport);
        }
        setTransportRestrictionStyles();
    }
}

function updateCityDropdown(county: string, addressComponent: HTMLElement, callback?: Function) {
    if (!county) return;

    const citiesUrl = window.config.getCitiesUrl + "?countyName=" + county;
    const cityDropdown = addressComponent.querySelector(".dropdown-city");
    const cityHidden = cityDropdown.previousElementSibling as HTMLInputElement;
    cityHidden.value = null;
    getDataText(citiesUrl, d => {
        const template = document.createElement("template");
        template.innerHTML = d;
        cityDropdown.parentElement.replaceChild(template.content, cityDropdown);

        // Reapplying the event listeners
        $$(".address__component").forEach(c => initAddressFields(c));

        const newDdl = addressComponent.querySelector(".dropdown-city");
        const items = newDdl.querySelectorAll(".dropdown__list-item");

        if (!!items.length && items.length < 2) {
            const firstOption = addressComponent.querySelector(".dropdown.dropdown-city input") as HTMLInputElement;
            if (firstOption) {
                const settlement = firstOption.value;
                cityHidden.value = settlement;
                const cityItem = addressComponent.querySelector(`.dropdown-city input[value="${settlement}"]`)?.parentElement;
                triggerDropdownClick(cityItem, false);
                updateSettlementDropdown(settlement, addressComponent);
            }
        }
        if (!!items.length) {
            showDropdown(newDdl);
        }

        if (callback) {
            callback();
        }
    });
}

function updateSettlementDropdown(city: string, addressComponent: HTMLElement, callback?: Function) {
    const settlementsUrl = window.config.getSettlementsUrl + "?cityName=" + city;
    const nextButton = get("shipping-next");
    nextButton?.setAttribute("disabled", "disabled");
    getDataText(settlementsUrl, d => {
        const template = document.createElement("template");
        template.innerHTML = d;
        const settlementDropdown = addressComponent.querySelector(".dropdown-settlement");
        settlementDropdown?.parentElement.replaceChild(template.content, settlementDropdown);

        // Reapplying the event listeners
        $$(".address__component").forEach(c => initAddressFields(c));

        const newDdl = addressComponent.querySelector(".dropdown-settlement");
        const items = newDdl ? newDdl.querySelectorAll(".dropdown__list-item") : [];


        if (items.length < 2) {
            hideDropdown(newDdl);
        } else {
            showDropdown(newDdl);
        }
        if (callback) {
            callback();
        }
    }).then(() => { nextButton?.removeAttribute("disabled"); });
}

function hideDropdown(ddl: Element) {
    if (!ddl) {
        return
    }
    ddl.closest(".manual-address").classList.add("manual-address__hidden");
    const input = ddl.previousElementSibling as HTMLInputElement;
    input.disabled = true;
}

function showDropdown(ddl: Element) {
    if (!ddl) {
        return
    }
    ddl.closest(".manual-address").classList.remove("manual-address__hidden");
    const input = ddl.previousElementSibling as HTMLInputElement;
    input.disabled = false;
}

function getAddressComponentPrefix(addressComponent: HTMLElement){
    const isAccount = get("_MyInfo");
    if(isAccount)
        return "AddressInfo";
    const addressComponentRoot = addressComponent.closest(".address__component");
    return addressComponentRoot.id.startsWith("billing") ? "BillingAddress" : "ShippingAddress";

}

export function initAddressSearch() {
    $$(".address__component").forEach(c => {
        if (c.hasAttribute("data-bound")) return
        c.setAttribute("data-bound", "");
        const inAdsComponent = c.querySelector(".address-search-component") as HTMLElement;
        if (!inAdsComponent) return;
        initAddressSearchComponent(c, inAdsComponent, updateAddressFields, queryAddresses);
        initAddressFields(c);
    });
}
